/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from "react";
import { addMonths, format, subMonths } from "date-fns";
import cx from "classnames";

import styles from "./date-picker.module.scss";
import { Button } from "../Button/Button";
import { Card } from "../Card/Card";
import { CalendarControls } from "../MiniCalendar/CalendarControls";
import { MiniCalendar } from "../MiniCalendar/MiniCalendar";
import { dateFormat, isSameDay, pickerContext, toDate } from "../utils";
import { Picker } from "../types";

export enum DaysOfWeek {
  sunday = 0,
  monday = 1,
  tuesday = 2,
  wednesday = 3,
  thursday = 4,
  friday = 5,
  saturday = 6
}

export namespace DatePicker {
  export type Props = Picker.BaseProps & {
    selectedDate: Picker.ISODateString;
    onUpdate(selectedDate: Picker.ISODateString): void;
  };
}

export const DatePicker = (props: DatePicker.Props): JSX.Element => {
  const {
    locale,
    firstDayOfWeek = DaysOfWeek[
      locale?.options?.weekStartsOn || 0
    ] as keyof typeof DaysOfWeek,
    onCancel,
    onUpdate,
    selectedDate,
    rtl = false,
    today = format(new Date(), dateFormat)
  } = props;

  const [tempSelectedDate, setTempSelectedDate] = useState<
    Picker.ISODateString
  >(selectedDate);
  const [visibleMonths, setVisibleMonths] = useState<Picker.ISODateString[]>([
    tempSelectedDate
  ]);

  const contextValue = {
    rtl,
    firstDayOfWeek,
    locale,
    tempSelectedDate,
    onNextMonth: async (): Promise<void> => {
      setVisibleMonths(
        visibleMonths.map(
          (date: Picker.ISODateString): Picker.ISODateString =>
            format(addMonths(toDate(date), 1), dateFormat)
        )
      );
    },
    onPrevMonth: async (): Promise<void> => {
      setVisibleMonths(
        visibleMonths.map(
          (date: Picker.ISODateString): Picker.ISODateString =>
            format(subMonths(toDate(date), 1), dateFormat)
        )
      );
    },
    onUpdate,
    isSelectedDate: (date: Picker.ISODateString): boolean =>
      isSameDay(date, tempSelectedDate),
    reset: () => {
      setTempSelectedDate(selectedDate);
      setVisibleMonths([selectedDate]);
    }
  };

  const calendarLabel = useMemo(
    () => format(toDate(visibleMonths[0]), "MMMM yyyy"),
    [visibleMonths]
  );

  return (
    <pickerContext.Provider value={contextValue}>
      <Card
        aria-label="Use arrow and page keys to navigate, and press space to select a day"
        className={styles.datePicker}
        surface="light"
      >
        <div className={styles.flexCol}>
          <Card padding={0}>
            <CalendarControls label={calendarLabel} />
            <div className={styles.justifyCenter}>
              {visibleMonths.map(
                (visibleMonth: Picker.ISODateString): JSX.Element => (
                  <MiniCalendar
                    {...props}
                    date={visibleMonth}
                    key={visibleMonth}
                    onSelection={(date: Picker.ISODateString): void => {
                      setTempSelectedDate(date);
                    }}
                    today={today}
                  />
                )
              )}
            </div>
          </Card>

          <div className={cx(styles.flexRow, styles.justifyEnd, styles.mt2)}>
            <Button
              theme="tertiary-transparent"
              onClick={() => {
                contextValue.reset();
                onCancel();
              }}
              padding="condensed"
            >
              Cancel
            </Button>
            <Button
              theme="secondary"
              disabled={isSameDay(selectedDate, tempSelectedDate)}
              onClick={() => onUpdate(tempSelectedDate)}
              padding="condensed"
            >
              Update
            </Button>
          </div>
        </div>
      </Card>
    </pickerContext.Provider>
  );
};
